import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMe } from '@/hooks/useMe';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useCreateTask } from '@/features/patient-record/hooks/useTasksMutations';
import type {
  PatientTaskCreateInput,
  PatientTaskPriority,
  PatientTaskType,
} from '@/features/patient-record/types';

import { TaskFormFields } from './TaskFormFields';
import type { TaskFormValues } from './TaskFormFields.types';
import { EMPTY_FORM_VALUES } from './taskHelpers';

export interface TaskCreateDefaults {
  readonly title?: string;
  readonly description?: string;
  readonly type?: PatientTaskType;
  readonly priority?: PatientTaskPriority;
}

interface TaskCreateDialogProps {
  readonly guid: string;
  readonly onClose: () => void;
  readonly defaults?: TaskCreateDefaults;
}

/**
 * Modal create-task form for the side-rail tasks panel (PTV2-1097).
 *
 * Uses the shared `TaskFormFields` block so the create and edit dialogs
 * stay UI-locked. Title is the only required field; assignee + due date
 * + notes are optional.
 */
export function TaskCreateDialog({
  guid,
  onClose,
  defaults,
}: TaskCreateDialogProps): ReactElement {
  const { t } = useTranslation();
  const create = useCreateTask(guid);
  const me = useMe();

  // Default the assignee to the current user — PRD Phase 2 quick win: most
  // tasks are captured for the clinician themselves, so this removes a
  // click. Falls back to null when `/me` isn't loaded yet (reseed-safety
  // adopts it once `/me` resolves, as long as the form is still untouched).
  const meAssignee =
    me.data !== undefined
      ? { guid: me.data.id, fullName: me.data.fullName, subtitle: me.data.email }
      : null;

  const toDraft = (): TaskFormValues => ({
    ...EMPTY_FORM_VALUES,
    assignee: meAssignee,
    ...(defaults?.title !== undefined ? { title: defaults.title } : {}),
    ...(defaults?.description !== undefined ? { description: defaults.description } : {}),
    ...(defaults?.type !== undefined ? { type: defaults.type } : {}),
    ...(defaults?.priority !== undefined ? { priority: defaults.priority } : {}),
  });

  const toInput = (d: TaskFormValues): PatientTaskCreateInput => ({
    title: d.title.trim(),
    type: d.type,
    priority: d.priority,
    assigneeId: d.assignee?.guid ?? null,
    dueDate: d.dueDate.length > 0 ? d.dueDate : null,
    description: d.description.trim().length > 0 ? d.description.trim() : null,
    notes: d.notes.trim().length > 0 ? d.notes.trim() : null,
  });

  // PTV2-1327: one primitive owns the draft. A create dialog is explicit-submit,
  // so autosave is off — the task is created only by the Create button. The
  // primitive registers while dirty so a refresh prompts instead of dropping input.
  const form = useEditableDraft<TaskFormValues>({
    initial: toDraft(),
    label: t('patient.tasks.createTitle'),
    autosave: false,
    canSave: (d) => d.title.trim().length > 0,
    onSave: (d) => create.mutateAsync(toInput(d)),
  });
  const { draft } = form;

  // Explicit submit: commit AND close.
  const submitAndClose = (): void => {
    if (!form.canSave) return;
    void create.mutateAsync(toInput(draft)).then(onClose).catch(() => undefined);
  };

  // PTV2-1301: guard dismiss / refresh while the form has input. The primitive
  // already registers, so the guard only intercepts dismissal + renders the confirm.
  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: create.isPending,
    label: t('patient.tasks.createTitle'),
    onSave: submitAndClose,
    onClose,
    register: false,
  });

  return (
    <>
    <Dialog open {...guard.rootProps}>
      <DialogContent className="max-w-2xl gap-3" {...guard.contentProps}>
        <DialogHeader>
          <DialogTitle>{t('patient.tasks.createTitle')}</DialogTitle>
          <DialogDescription>
            {t('patient.tasks.createDescription')}
          </DialogDescription>
        </DialogHeader>
        <form
          ref={form.containerProps.ref}
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            submitAndClose();
          }}
        >
          <TaskFormFields
            values={draft}
            onChange={(next) => {
              form.setDraft(next);
            }}
            showStatus={false}
            disabled={create.isPending}
          />

          {create.isError && (
            <p role="alert" className="text-xs text-destructive">
              {t('patient.tasks.errorSave')}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={guard.requestClose}
              disabled={create.isPending}
            >
              {t('patient.tasks.cancel')}
            </Button>
            <Button type="submit" disabled={!form.canSave || create.isPending}>
              {create.isPending
                ? t('patient.tasks.saving')
                : t('patient.tasks.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    {guard.confirm}
    </>
  );
}
